import React, { useState, useEffect } from 'react';
import { ExampleWriting, Score, WritingPrompt } from '../types/models';
import '../styles/ExampleWritings.css';

interface ExampleWritingsProps {
  yearLevel: number;
  promptId?: string;
  studentWriting?: string;
  onBack?: () => void;
}

/**
 * Component for displaying example writings with scores, feedback, and techniques.
 * Implements Requirements 6.1, 6.2, 6.3, 6.4
 * 
 * - Displays examples with scores and feedback (6.2)
 * - Shows quality level indicators (6.3)
 * - Highlights techniques and elements (6.4)
 */
export const ExampleWritings: React.FC<ExampleWritingsProps> = ({
  yearLevel,
  promptId,
  studentWriting,
  onBack,
}) => {
  const [prompts, setPrompts] = useState<WritingPrompt[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<string>(promptId || '');
  const [selectedPrompt, setSelectedPrompt] = useState<WritingPrompt | null>(null);
  const [examples, setExamples] = useState<ExampleWriting[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [promptsLoading, setPromptsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [selectedQuality, setSelectedQuality] = useState<string>('all');
  const [selectedExample, setSelectedExample] = useState<ExampleWriting | null>(null);
  const [showComparison, setShowComparison] = useState<boolean>(false);
  const [testFormat, setTestFormat] = useState<'edutest' | 'acer'>('edutest');
  const [category, setCategory] = useState<string>('creative');

  // Get auth token from localStorage
  const getAuthToken = (): string | null => {
    try {
      const storedTokens = localStorage.getItem('authTokens');
      if (storedTokens) {
        const tokens = JSON.parse(storedTokens);
        return tokens.idToken;
      }
    } catch (err) {
      console.error('Failed to get auth token:', err);
    }
    return null;
  };

  // Fetch prompts on mount and when filters change
  useEffect(() => {
    fetchPrompts();
  }, [yearLevel, testFormat, category]);

  // Reset selected prompt when filters change
  useEffect(() => {
    setSelectedPromptId('');
    setSelectedPrompt(null);
    setExamples([]);
  }, [testFormat, category]);

  // Fetch examples when prompt is selected
  useEffect(() => {
    if (selectedPromptId) {
      fetchExamples();
      // Find and set the selected prompt details
      const prompt = prompts.find(p => p.id === selectedPromptId);
      setSelectedPrompt(prompt || null);
    } else {
      setExamples([]);
      setSelectedPrompt(null);
      setLoading(false);
    }
  }, [selectedPromptId, selectedQuality, prompts]);

  const fetchPrompts = async () => {
    setPromptsLoading(true);
    setError('');

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const authToken = getAuthToken();
      
      if (!authToken) {
        throw new Error('Not authenticated');
      }
      
      // Fetch prompts for the year level, test format, and category
      const response = await fetch(`${apiUrl}/api/prompts?yearLevel=${yearLevel}&testFormat=${testFormat}&category=${category}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load prompts');
      }

      const fetchedPrompts = (data.prompts || []).map((p: any) => ({
        ...p,
        id: p.id || p.promptId,
      }));
      setPrompts(fetchedPrompts);
      
      // If promptId was passed as prop, select it
      if (promptId && !selectedPromptId) {
        setSelectedPromptId(promptId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setPromptsLoading(false);
      if (!promptId) {
        setLoading(false);
      }
    }
  };

  const fetchExamples = async () => {
    if (!selectedPromptId) {
      setExamples([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const authToken = getAuthToken();
      
      if (!authToken) {
        throw new Error('Not authenticated');
      }
      
      let url = `${apiUrl}/api/examples?yearLevel=${yearLevel}&promptId=${selectedPromptId}`;
      if (selectedQuality !== 'all') {
        url += `&qualityLevel=${selectedQuality}`;
      }

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load examples');
      }

      // Sort examples by quality level: high -> medium -> low
      const qualityOrder: Record<string, number> = { high: 1, medium: 2, low: 3 };
      const sortedExamples = (data.examples || []).sort((a: ExampleWriting, b: ExampleWriting) => {
        return (qualityOrder[a.qualityLevel] || 4) - (qualityOrder[b.qualityLevel] || 4);
      });

      setExamples(sortedExamples);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getQualityBadgeClass = (quality: string): string => {
    const classes: Record<string, string> = {
      high: 'quality-badge--high',
      medium: 'quality-badge--medium',
      low: 'quality-badge--low',
    };
    return classes[quality] || '';
  };

  const getQualityLabel = (quality: string): string => {
    const labels: Record<string, string> = {
      high: 'High Quality',
      medium: 'Medium Quality',
      low: 'Needs Improvement',
    };
    return labels[quality] || quality;
  };

  const getScoreColor = (score: number): string => {
    if (score >= 9) return '#4caf50';
    if (score >= 7) return '#8bc34a';
    if (score >= 5) return '#ffc107';
    if (score >= 3) return '#ff9800';
    return '#f44336';
  };

  const calculateAverageScore = (scores: Score[]): number => {
    if (!scores || scores.length === 0) return 0;
    const total = scores.reduce((sum, s) => sum + s.value, 0);
    return Math.round((total / scores.length) * 10) / 10;
  };

  const formatCriterionName = (criterion: string): string => {
    const names: Record<string, string> = {
      punctuation: 'Punctuation',
      creativity: 'Creativity',
      construction: 'Construction',
      grammar: 'Grammar',
      spelling: 'Spelling',
      relevanceToTask: 'Relevance to Task',
    };
    return names[criterion] || criterion.replace(/([A-Z])/g, ' $1').trim();
  };

  const renderExampleCard = (example: ExampleWriting) => {
    const avgScore = calculateAverageScore(example.associatedScores);
    
    return (
      <div 
        key={example.id} 
        className="example-card"
        onClick={() => setSelectedExample(example)}
      >
        <div className="example-card__header">
          <span className={`quality-badge ${getQualityBadgeClass(example.qualityLevel)}`}>
            {getQualityLabel(example.qualityLevel)}
          </span>
          <span className="example-card__year">Year {example.yearLevel}</span>
        </div>
        
        <div className="example-card__score">
          <span 
            className="example-card__score-value"
            style={{ color: getScoreColor(avgScore) }}
          >
            {avgScore}
          </span>
          <span className="example-card__score-max">/10</span>
          <span className="example-card__score-label">Average Score</span>
        </div>
        
        <div className="example-card__preview">
          {example.content.substring(0, 150)}...
        </div>
        
        {example.techniques && example.techniques.length > 0 && (
          <div className="example-card__techniques">
            <span className="techniques-label">Key Techniques:</span>
            <div className="techniques-tags">
              {example.techniques.slice(0, 3).map((technique, index) => (
                <span key={index} className="technique-tag">{technique}</span>
              ))}
              {example.techniques.length > 3 && (
                <span className="technique-tag technique-tag--more">
                  +{example.techniques.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}
        
        <button className="example-card__view-btn">
          View Full Example →
        </button>
      </div>
    );
  };

  const renderDetailedView = () => {
    if (!selectedExample) return null;

    return (
      <div className="example-detail">
        <div className="example-detail__header">
          <button 
            className="btn-back" 
            onClick={() => setSelectedExample(null)}
          >
            ← Back to Examples
          </button>
          <div className="example-detail__badges">
            <span className={`quality-badge ${getQualityBadgeClass(selectedExample.qualityLevel)}`}>
              {getQualityLabel(selectedExample.qualityLevel)}
            </span>
            <span className="year-badge">Year {selectedExample.yearLevel}</span>
          </div>
        </div>

        {/* Comparison Toggle */}
        {studentWriting && (
          <div className="comparison-toggle">
            <button
              className={`toggle-btn ${!showComparison ? 'toggle-btn--active' : ''}`}
              onClick={() => setShowComparison(false)}
            >
              Example Only
            </button>
            <button
              className={`toggle-btn ${showComparison ? 'toggle-btn--active' : ''}`}
              onClick={() => setShowComparison(true)}
            >
              Compare with Your Writing
            </button>
          </div>
        )}

        <div className={`example-detail__content ${showComparison ? 'comparison-mode' : ''}`}>
          {/* Example Writing */}
          <div className="writing-panel">
            <h3 className="writing-panel__title">
              {showComparison ? 'Example Writing' : 'Full Example'}
            </h3>
            <div className="writing-content">
              {selectedExample.content.split('\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>

          {/* Student Writing (Comparison Mode) */}
          {showComparison && studentWriting && (
            <div className="writing-panel writing-panel--student">
              <h3 className="writing-panel__title">Your Writing</h3>
              <div className="writing-content">
                {studentWriting.split('\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Scores Section */}
        <div className="example-detail__scores">
          <h3 className="section-title">Scores & Feedback</h3>
          <div className="scores-grid">
            {selectedExample.associatedScores.map((score) => (
              <div key={score.criterion} className="score-card">
                <div className="score-card__header">
                  <span className="score-card__name">
                    {formatCriterionName(score.criterion)}
                  </span>
                  <span 
                    className="score-card__value"
                    style={{ color: getScoreColor(score.value) }}
                  >
                    {score.value}/10
                  </span>
                </div>
                <div className="score-card__bar">
                  <div 
                    className="score-card__bar-fill"
                    style={{ 
                      width: `${(score.value / 10) * 100}%`,
                      backgroundColor: getScoreColor(score.value)
                    }}
                  />
                </div>
                <p className="score-card__explanation">{score.explanation}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Techniques Section */}
        {selectedExample.techniques && selectedExample.techniques.length > 0 && (
          <div className="example-detail__techniques">
            <h3 className="section-title">
              <span className="section-icon">✨</span>
              Writing Techniques Demonstrated
            </h3>
            <div className="techniques-list">
              {selectedExample.techniques.map((technique, index) => (
                <div key={index} className="technique-item">
                  <span className="technique-bullet">•</span>
                  <span className="technique-text">{technique}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Explanation Section */}
        <div className="example-detail__explanation">
          <h3 className="section-title">
            <span className="section-icon">📝</span>
            Why This Writing Received This Score
          </h3>
          <p className="explanation-text">{selectedExample.explanation}</p>
        </div>
      </div>
    );
  };

  const renderQualityFilter = () => (
    <div className="quality-filter">
      <span className="filter-label">Filter by Quality:</span>
      <div className="filter-buttons">
        {['all', 'high', 'medium', 'low'].map((quality) => (
          <button
            key={quality}
            className={`filter-btn ${selectedQuality === quality ? 'filter-btn--active' : ''}`}
            onClick={() => setSelectedQuality(quality)}
          >
            {quality === 'all' ? 'All' : getQualityLabel(quality)}
          </button>
        ))}
      </div>
    </div>
  );

  const renderPromptSelector = () => (
    <div className="prompt-selector-section">
      <div className="prompt-filters-row">
        <div className="filter-group">
          <label className="filter-label">Test Format:</label>
          <select
            className="filter-select"
            value={testFormat}
            onChange={(e) => setTestFormat(e.target.value as 'edutest' | 'acer')}
          >
            <option value="edutest">Edutest</option>
            <option value="acer">ACER</option>
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Category:</label>
          <select
            className="filter-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="creative">Creative</option>
            <option value="persuasive">Persuasive</option>
            <option value="narrative">Narrative</option>
          </select>
        </div>
      </div>
      
      <div className="prompt-selector-header">
        <label className="prompt-selector-label">Select a Writing Prompt:</label>
        <select
          className="prompt-dropdown"
          value={selectedPromptId}
          onChange={(e) => setSelectedPromptId(e.target.value)}
          disabled={promptsLoading}
        >
          <option value="">-- Choose a prompt to see examples ({prompts.length} available) --</option>
          {prompts.map((prompt, index) => (
            <option key={prompt.id} value={prompt.id}>
              {index + 1}. {prompt.promptText.substring(0, 80)}...
            </option>
          ))}
        </select>
      </div>
      
      {selectedPrompt && (
        <div className="selected-prompt-display">
          <div className="prompt-display-header">
            <span className="prompt-badge">{selectedPrompt.category}</span>
            <span className="prompt-badge prompt-badge--format">{selectedPrompt.testFormat?.toUpperCase()}</span>
            <span className="prompt-time">{selectedPrompt.timeLimit} min</span>
          </div>
          <p className="prompt-text-full">{selectedPrompt.promptText}</p>
          {selectedPrompt.context && (
            <p className="prompt-context">{selectedPrompt.context}</p>
          )}
          {selectedPrompt.instructions && (
            <p className="prompt-instructions">{selectedPrompt.instructions}</p>
          )}
        </div>
      )}
    </div>
  );

  if (promptsLoading) {
    return (
      <div className="example-writings example-writings--loading">
        <div className="loading-spinner" />
        <p>Loading prompts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="example-writings example-writings--error">
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={fetchExamples} className="btn-retry">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show detailed view if an example is selected
  if (selectedExample) {
    return (
      <div className="example-writings">
        {renderDetailedView()}
      </div>
    );
  }

  return (
    <div className="example-writings">
      {/* Header */}
      <div className="example-writings__header">
        <div className="header-left">
          {onBack && (
            <button onClick={onBack} className="btn-back" aria-label="Go back">
              ← Back
            </button>
          )}
          <div className="header-info">
            <h2 className="header-title">Example Writings</h2>
            <p className="header-subtitle">
              Year {yearLevel} • Select a prompt to view examples
            </p>
          </div>
        </div>
      </div>

      {/* Prompt Selector */}
      {renderPromptSelector()}

      {/* Show content only when a prompt is selected */}
      {selectedPromptId && (
        <>
          {/* Quality Filter */}
          {renderQualityFilter()}

          {/* Loading state for examples */}
          {loading ? (
            <div className="examples-loading">
              <div className="loading-spinner" />
              <p>Loading examples...</p>
            </div>
          ) : examples.length === 0 ? (
            <div className="no-examples">
              <span className="no-examples__icon">📚</span>
              <h3>No Examples Found</h3>
              <p>
                {selectedQuality !== 'all' 
                  ? `No ${getQualityLabel(selectedQuality).toLowerCase()} examples available for this prompt.`
                  : `No example writings available for this prompt yet.`
                }
              </p>
              {selectedQuality !== 'all' && (
                <button 
                  className="btn-secondary"
                  onClick={() => setSelectedQuality('all')}
                >
                  Show All Examples
                </button>
              )}
            </div>
          ) : (
            <div className="examples-grid">
              {examples.map(renderExampleCard)}
            </div>
          )}
        </>
      )}

      {/* Prompt not selected message */}
      {!selectedPromptId && (
        <div className="no-prompt-selected">
          <span className="no-prompt-selected__icon">📝</span>
          <h3>Select a Prompt</h3>
          <p>Choose a writing prompt from the dropdown above to view example writings at different quality levels.</p>
        </div>
      )}

      {/* Info Section */}
      <div className="example-info">
        <h4 className="info-title">💡 How to Use Example Writings</h4>
        <ul className="info-list">
          <li>Select a prompt to see example writings for that specific topic</li>
          <li>Compare high, medium, and low quality examples to understand what makes writing effective</li>
          <li>Study the techniques used in high-quality examples</li>
          <li>Read the feedback to understand how each criterion is assessed</li>
        </ul>
      </div>
    </div>
  );
};

export default ExampleWritings;
